import React from 'react';
import { IResource } from '../../../api';
import { MapCanvasValue } from '../components/map/contexts/MapCanvasContext';
import { Shape, SpotShape } from '../types';

/**
 * Parse a floor's map_data and stamp the canonical resource labels/names
 * onto the spot shapes.
 *
 * Identity discipline matters here: the result feeds `ShapeSVG`'s
 * per-shape memo, so every object that didn't actually change must keep
 * its reference. The old implementation re-parsed and re-mapped the whole
 * shapes array once per resource — every schedules refetch gave all spots
 * (and groups) new identities and re-rendered the entire map.
 */
export const useMapBuilderMappedResourcesData = (
  floor?: IResource,
  resources?: IResource[]
) => {
  // Parse once per map_data string — the parsed objects are the stable
  // identity baseline that label stamping preserves where possible.
  const parsedMapData = React.useMemo(() => {
    if (!floor?.map_data) return undefined;
    return JSON.parse(floor.map_data) as MapCanvasValue;
  }, [floor?.map_data]);

  return React.useMemo(() => {
    if (!floor) return undefined;
    if (!parsedMapData || !resources) return parsedMapData;
    return applyResourceLabels(parsedMapData, resources);
  }, [floor, parsedMapData, resources]);
};

/**
 * Stamps the canonical resource label and name onto every spot that
 * references a resource, keeping the identity of everything unchanged.
 * Shared with the editor's preview so it shows what the map page shows.
 */
export const applyResourceLabels = (
  mapData: MapCanvasValue,
  resources: IResource[]
): MapCanvasValue => {
  const resourceById = new Map(
    resources.map((resource) => [resource.id, resource])
  );

  const applyResourceData = (shape: Shape): Shape => {
    if (shape.type === 'spot') {
      const spot = shape as SpotShape;
      if (!spot.resource_id) return shape;
      const resource = resourceById.get(spot.resource_id);
      if (!resource) return shape;
      const label = resource.label || '';
      const name = resource.name;
      if (spot.label === label && spot.name === name) return shape;
      return { ...spot, label, name };
    }

    if (shape.type === 'group' && Array.isArray(shape.children)) {
      const children = shape.children.map(applyResourceData);
      const hasChanged = children.some(
        (child, index) => child !== shape.children[index]
      );
      return hasChanged ? { ...shape, children } : shape;
    }

    return shape;
  };

  const shapes = mapData.shapes.map(applyResourceData);
  const hasChanged = shapes.some(
    (shape, index) => shape !== mapData.shapes[index]
  );

  return hasChanged ? { ...mapData, shapes } : mapData;
};
